import React from 'react'
import { ShareForSettingsModal } from '../types'
import { useGetShareMembershipsQuery } from '@sketch/gql-types'
import { Box } from '@sketch/components'
import InfiniteList, { handleFetchMore } from 'components/InfiniteList'
import { ShareMember, ShareMemberPlaceholder } from '../ShareMember'

export type ShareForMembershipList = Pick<
  ShareForSettingsModal,
  | 'identifier'
  | 'userCanUpdateCommentsEnabled'
  | 'userCanUpdateSettings'
  | 'type'
  | 'parentAccessLevel'
  | 'isDraft'
  | 'name'
  | 'memberships'
>

interface MembershipListProps {
  share: ShareForMembershipList
}

const PAGE_SIZE = 20

const buildPlaceholderArray = (count: number) => (
  <>
    {new Array(count).fill(null).map((_, index) => (
      <ShareMemberPlaceholder key={index} />
    ))}
  </>
)

const ShareMemberships = ({ share }: MembershipListProps) => {
  const membershipCount = share.memberships?.meta.totalCount

  const { error, data, loading, fetchMore } = useGetShareMembershipsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      shareIdentifier: share.identifier,
    },
  })

  const memberships = data?.share?.memberships?.entries || []
  const myMembership = data?.share?.myMembership

  if (error) {
    return (
      <Box my={4} color="red">
        Memberships failed to load
      </Box>
    )
  }

  if (loading && memberships.length === 0) {
    return buildPlaceholderArray(Math.min(membershipCount || 0, PAGE_SIZE))
  }

  if (memberships.length === 0) {
    return null
  }

  const after = data?.share?.memberships?.meta.after || null
  const placeholderMembers = (membershipCount || PAGE_SIZE) - memberships.length

  return (
    <InfiniteList
      wrappingComponent={React.Fragment}
      onLoadMore={handleFetchMore(
        fetchMore,
        ['share', 'memberships', 'entries'],
        { after }
      )}
      renderLoading={() => null}
    >
      {memberships.map(membership => (
        <ShareMember
          key={membership.identifier}
          share={share}
          member={membership}
          isMyMembership={myMembership?.identifier === membership.identifier}
        />
      ))}
      {loading &&
        buildPlaceholderArray(Math.min(PAGE_SIZE, placeholderMembers))}
    </InfiniteList>
  )
}

export default ShareMemberships
